import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const OVERFLOW_THRESHOLD = 1;
const END_THRESHOLD = 20;
const DEFAULT_SCROLL_END_DEBOUNCE_MS = 200;

export function useScrollAreaOverflow({
  active = true,
  observeDeps = [],
  scrollEndDebounceMs = DEFAULT_SCROLL_END_DEBOUNCE_MS,
} = {}) {
  const scrollAreaRef = useRef(null);
  const scrollContentRef = useRef(null);
  const scrollDebounceRef = useRef(null);
  const [hasScroll, setHasScroll] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  const measure = useCallback(() => {
    const el = scrollAreaRef.current;
    if (!el) {
      setHasScroll(false);
      return;
    }
    const has = el.scrollHeight > el.clientHeight + OVERFLOW_THRESHOLD;
    setHasScroll(has);
    setAtEnd(has && el.scrollTop + el.clientHeight >= el.scrollHeight - END_THRESHOLD);
  }, []);

  const onScroll = useCallback(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const has = el.scrollHeight > el.clientHeight + OVERFLOW_THRESHOLD;
    if (has) {
      setAtEnd(el.scrollTop + el.clientHeight >= el.scrollHeight - END_THRESHOLD);
    } else {
      setAtEnd(false);
    }
    setIsScrolling(true);
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current);
    }
    scrollDebounceRef.current = setTimeout(() => {
      setIsScrolling(false);
      scrollDebounceRef.current = null;
    }, scrollEndDebounceMs);
  }, [scrollEndDebounceMs]);

  useLayoutEffect(() => {
    if (!active) {
      setHasScroll(false);
      setAtEnd(false);
      setIsScrolling(false);
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
        scrollDebounceRef.current = null;
      }
      return;
    }
    measure();
    const scrollEl = scrollAreaRef.current;
    const contentEl = scrollContentRef.current;
    if (!scrollEl) return;

    const ro = new ResizeObserver(() => measure());
    ro.observe(scrollEl);
    if (contentEl) {
      ro.observe(contentEl);
    }

    window.addEventListener('resize', measure);
    return () => {
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
        scrollDebounceRef.current = null;
      }
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [active, measure, ...observeDeps]);

  return {
    scrollAreaRef,
    scrollContentRef,
    hasScroll,
    atEnd,
    isScrolling,
    onScroll,
  };
}
